import { useCallback, useMemo } from 'react'
import useLocalStorage from '@/hooks/useLocalStorage'
import { RowValueFlag } from '@/types/flags'
import { getFlagsCount } from '@/utils/flags'
import { focusOnField } from '@/utils/row-values'
import { useDocumentContext } from '@/components/image-zoom-pan/providers/DocumentProvider'
import { useDocumentRowValuesContext } from '@/components/validation/providers/DocumentRowValuesProvider'
import { OptionsKeyType } from '@/components/validation/VisibleFieldsControl'
import useFlagPriorityColor from './useFlagPriorityColor'

export default function useFullFlagListData() {
  const [selectedOptionKey, setSelectedOptionKey] =
    useLocalStorage<OptionsKeyType>(
      'validation-visible-fields-option',
      'relevant',
    )

  const { rowValueFlags } = useDocumentRowValuesContext()
  const { document } = useDocumentContext()
  const { document_flags = [] } = document || {}

  const documentFlagsCount = getFlagsCount(document_flags)
  const rowFlagsCount = getFlagsCount(rowValueFlags)

  const { color } = useFlagPriorityColor({
    documentFlags: documentFlagsCount,
    rowsFlags: rowFlagsCount,
  })

  const totalLength = document_flags.length + rowValueFlags.length

  const onRowValueFlagClick = useCallback(
    (
      flag: RowValueFlag,
      cellToFocus: HTMLInputElement | HTMLDivElement | null,
    ) => {
      if (!cellToFocus && selectedOptionKey === 'relevant') {
        setSelectedOptionKey('all')
        setTimeout(() => {
          focusOnField(flag.document_row_value_id)
        }, 0)
      }
    },
    [selectedOptionKey, setSelectedOptionKey],
  )

  return useMemo(
    () => ({
      color,
      documentFlags: document_flags,
      rowValueFlags,
      totalLength,
      onRowValueFlagClick,
    }),
    [color, document_flags, rowValueFlags, totalLength, onRowValueFlagClick],
  )
}
